import { Button, Flex, Text, useToast } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { createCategoriesActivitiesApi } from './../../../utils/api-interface'
import { useTranslation } from 'react-i18next'
import { log } from '../../../utils/logger'
import { ActivitySuggestionList, Category, CategoryListResponse, ConfirmActivitiesSuggestedList } from '../../../services/network'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { DeleteConfirmationDialog } from '../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog'
import { SuggestedActivity } from '../../../components/SuggestedActivity/SuggestedActivity'
import { Blocker } from 'react-router-dom'
import { getErrorMessage } from '../../../utils/utils'

interface IProposalPageProps {
  blocker: Blocker
  categories: CategoryListResponse
  confirmSuggestedActivitiesList: ConfirmActivitiesSuggestedList | undefined
  setConfirmSuggestedActivitiesList: React.Dispatch<React.SetStateAction<ConfirmActivitiesSuggestedList | undefined>>
}

function ProposalPage({ blocker, confirmSuggestedActivitiesList, categories, setConfirmSuggestedActivitiesList }: IProposalPageProps): JSX.Element {
  const { t } = useTranslation()
  const toast = useToast()

  const [suggestedActivities, setSuggestedActivities] = useState<ActivitySuggestionList>()
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)
  const [loadingUpdate, isLoadingUpdate] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const handleCloseDialog = (): void => {
    setIsDialogOpen(false)
  }

  const getProposals = async (): Promise<void> => {
    const categoriesActivitiesApi = createCategoriesActivitiesApi()
    categoriesActivitiesApi
      .getSuggestedActivities()
      .then((response) => {
        setSuggestedActivities(response.data)
      })
      .catch((error) => {
        log.error('Error fetching activities suggestes:', error)
      })
  }

  const sendConfirmedActivities = async (): Promise<void> => {
    if (loadingUpdate) {
      return
    }
    if (confirmSuggestedActivitiesList) {
      isLoadingUpdate(true)
      try {
        const categoriesActivitiesApi = createCategoriesActivitiesApi()
        const response = await categoriesActivitiesApi.activitySuggestionConfirmation(confirmSuggestedActivitiesList)
        log.debug(response)
        setConfirmSuggestedActivitiesList(undefined)
        toast({ title: t('suggestions_approval_success'), status: 'success', duration: 4000, isClosable: true })
        getProposals()
      } catch (error: any) {
        toast({ title: t('error_suggestions_approval'), description: getErrorMessage(error), status: 'error', duration: 4000, isClosable: true })
      }
    }
    isLoadingUpdate(false)
  }

  const alertUser = (e: { preventDefault: () => void; returnValue: string }): void => {
    e.preventDefault()
    e.returnValue = ''
  }
  const handleSave = (): void => {
    sendConfirmedActivities()
    setIsDialogOpen(false)
  }

  useEffect(() => {
    getProposals()
    eva.replace()
  }, [])

  useEffect(() => {
    if (confirmSuggestedActivitiesList?.confirmActivitiesSuggested && confirmSuggestedActivitiesList.confirmActivitiesSuggested.length >= 1) {
      setSaveButtonDisabled(false)
      window.addEventListener('beforeunload', alertUser)
    }
    if (
      confirmSuggestedActivitiesList === undefined ||
      (confirmSuggestedActivitiesList?.confirmActivitiesSuggested && confirmSuggestedActivitiesList.confirmActivitiesSuggested.length === 0)
    ) {
      setSaveButtonDisabled(true)
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      window.removeEventListener('beforeunload', alertUser)
    }
    return () => {
      window.removeEventListener('beforeunload', alertUser)
    }
  }, [confirmSuggestedActivitiesList, suggestedActivities])

  const handleDialogConfirm = (): void => {
    setConfirmSuggestedActivitiesList(undefined)
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  return (
    <Flex flexDirection={'column'} justifyContent={'space-between'} position={'absolute'} top={0} bottom={0} left={0} right={0}>
      <Flex direction={'column'} overflow='auto' position={'absolute'} top={0} bottom={'80px'} left={0} right={0}>
        {suggestedActivities?.activities && suggestedActivities?.activities.length > 0 ? (
          suggestedActivities.activities.map((activity) => (
            <SuggestedActivity
              key={activity.id}
              category={categories.categories.find((c: Category) => c.id === activity.categoryId)}
              activity={activity}
              confirmSuggestedActivitiesList={confirmSuggestedActivitiesList}
              setConfirmSuggestedActivitiesList={setConfirmSuggestedActivitiesList}
              isLoading={loadingUpdate}
            />
          ))
        ) : (
          <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
            {t('empty_suggestions_approval')}
          </Text>
        )}
      </Flex>
      <Flex direction={'row-reverse'} marginTop={'10px'} position={'absolute'} left={0} right={0} bottom={'20px'}>
        {!loadingUpdate ? (
          <Button
            variant={'primary'}
            width={'211px'}
            onClick={(): void => {
              setIsDialogOpen(true)
            }}
            isDisabled={saveButtonDisabled}>
            {t('save')}
          </Button>
        ) : !saveButtonDisabled ? (
          <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
            {t('operation_in_progress', { operation: t('suggestions_approval_op') })}
          </Text>
        ) : (
          <></>
        )}
      </Flex>
      <DeleteConfirmationDialog isOpen={isDialogOpen} onClose={handleCloseDialog} onConfirm={handleSave} type='approve_activity' />
      {blocker.state === 'blocked' && (
        <DeleteConfirmationDialog
          isOpen={blocker.state === 'blocked'}
          onClose={(): void => blocker.reset()}
          onConfirm={(): void => handleDialogConfirm()}
          type='blocker'
        />
      )}
    </Flex>
  )
}

export { ProposalPage }
